import { useState, type CSSProperties } from "react";
import { Mail, Send, Reply, X } from "lucide-react";
import { toast } from "sonner";
import {
  sendAvocatCorrespondance,
  transitionAvocatCorrespondance,
  type AvocatCorrespondance,
  type AvocatDossierLink,
} from "@/lib/avocatApi";
import {
  STATUTS_MESSAGE,
  TRANSITIONS,
  TYPES_MESSAGE,
  VISIBILITES,
  VISIBILITE_DEFAUT,
  typeMessageLabel,
  visibiliteImposee,
  type StatutMessage,
  type TypeMessage,
  type VisibiliteMessage,
} from "@/lib/correspondanceCanonical";

/**
 * Correspondance protocolaire avocat ↔ avocat — modèle canonique V2.
 *
 * 16 types OHADA, 8 statuts (machine à états), 4 visibilités (l'arbitre n'est
 * jamais émetteur ; il n'apparaît qu'en copie/requis). Justification
 * obligatoire pour AUTRE, visibilité imposée pour les types « requis tribunal »,
 * fil de discussion (répondre). Endpoints backend /api/v1/avocat/correspondance
 * (+ /{id}/statut). Cf. correspondanceCanonical.ts (miroir backend).
 */
const GOLD = "oklch(0.695 0.105 68)";
const GRAPHITE = "oklch(0.18 0.02 243)";
const BORDER = "color-mix(in oklch, oklch(0.695 0.105 68) 18%, transparent)";

const inputStyle: CSSProperties = {
  background: GRAPHITE,
  color: "oklch(1 0 0)",
  borderColor: BORDER,
};

// Transitions réservées au destinataire / à l'expéditeur (miroir backend).
const TRANSITIONS_DESTINATAIRE: StatutMessage[] = [
  "DELIVRE",
  "LU",
  "ACCUSE_RECEPTION",
];
const TRANSITIONS_EXPEDITEUR: StatutMessage[] = ["RETIRE"];

function peutTransitionner(
  cible: StatutMessage,
  m: AvocatCorrespondance,
  currentAvocatId: string | undefined,
): boolean {
  if (!currentAvocatId) return false;
  if (TRANSITIONS_DESTINATAIRE.includes(cible)) {
    return m.destinataire_id === currentAvocatId;
  }
  if (TRANSITIONS_EXPEDITEUR.includes(cible)) {
    return m.expediteur_id === currentAvocatId;
  }
  return (
    m.expediteur_id === currentAvocatId || m.destinataire_id === currentAvocatId
  );
}

export function AvocatCorrespondancePanel({
  dossiers,
  messages,
  currentAvocatId,
  onSent,
}: {
  dossiers: AvocatDossierLink[];
  messages: AvocatCorrespondance[];
  currentAvocatId?: string;
  onSent: () => void;
}) {
  const [dossierId, setDossierId] = useState<number | "">("");
  const [destinataire, setDestinataire] = useState("");
  const [type, setType] = useState<TypeMessage>(TYPES_MESSAGE[0].value);
  const [sousType, setSousType] = useState("");
  const [visibilite, setVisibilite] = useState<VisibiliteMessage>(
    VISIBILITE_DEFAUT[TYPES_MESSAGE[0].value],
  );
  const [content, setContent] = useState("");
  const [replyTo, setReplyTo] = useState<AvocatCorrespondance | null>(null);
  const [busy, setBusy] = useState(false);

  const imposee = visibiliteImposee(type);
  const visibiliteEffective = imposee ? VISIBILITE_DEFAUT[type] : visibilite;
  const autreManque = type === "AUTRE" && sousType.trim().length < 2;

  const onChangeType = (next: TypeMessage) => {
    setType(next);
    setVisibilite(VISIBILITE_DEFAUT[next]); // réaligne sur le défaut du type
  };

  const canSend =
    typeof dossierId === "number" &&
    destinataire.trim().length >= 8 &&
    content.trim().length >= 2 &&
    !autreManque;

  const onSubmit = async () => {
    if (busy) return;
    if (typeof dossierId !== "number") {
      toast.error("Choisissez un dossier.");
      return;
    }
    if (destinataire.trim().length < 8) {
      toast.error("Identifiant du confrère requis (UUID).");
      return;
    }
    if (autreManque) {
      toast.error("Justification (sous-type) obligatoire pour « Autre ».");
      return;
    }
    setBusy(true);
    try {
      await sendAvocatCorrespondance({
        dossier_id: dossierId,
        destinataire_id: destinataire.trim(),
        type_message: type,
        content: content.trim(),
        sous_type: type === "AUTRE" ? sousType.trim() : null,
        visibilite: visibiliteEffective,
        reply_to_id: replyTo?.id ?? null,
      });
      toast.success("Message protocolaire envoyé.");
      setContent("");
      setSousType("");
      setReplyTo(null);
      onSent();
    } catch {
      toast.error("Échec de l'envoi (vérifiez l'identifiant du confrère).");
    } finally {
      setBusy(false);
    }
  };

  const onTransition = async (m: AvocatCorrespondance, cible: StatutMessage) => {
    try {
      await transitionAvocatCorrespondance(m.id, cible);
      toast.success(`Statut → ${STATUTS_MESSAGE[cible]}`);
      onSent();
    } catch {
      toast.error("Transition refusée.");
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border p-3 space-y-2" style={{ borderColor: BORDER }}>
        <div className="text-xs" style={{ color: GOLD }}>
          Nouveau message protocolaire
        </div>
        {dossiers.length === 0 ? (
          <p className="text-sm" style={{ opacity: 0.7 }}>
            Aucun dossier rattaché.
          </p>
        ) : (
          <>
            {replyTo && (
              <div
                className="flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px]"
                style={{ borderColor: BORDER, opacity: 0.85 }}
              >
                <Reply size={12} style={{ color: GOLD }} />
                Réponse à : {typeMessageLabel(replyTo.type_message)}
                <button
                  type="button"
                  className="ml-auto"
                  onClick={() => setReplyTo(null)}
                  aria-label="Annuler la réponse"
                >
                  <X size={12} />
                </button>
              </div>
            )}
            <select
              className="block w-full rounded-md border px-2 py-1.5 text-sm"
              style={inputStyle}
              value={dossierId === "" ? "" : String(dossierId)}
              onChange={(e) =>
                setDossierId(e.target.value === "" ? "" : Number(e.target.value))
              }
            >
              <option value="">— Dossier —</option>
              {dossiers.map((d) => (
                <option key={d.id} value={String(d.dossier_id)}>
                  Dossier #{d.dossier_id} ({d.partie})
                </option>
              ))}
            </select>
            <input
              className="block w-full rounded-md border px-2 py-1.5 text-sm"
              style={inputStyle}
              placeholder="Identifiant du confrère (UUID)"
              value={destinataire}
              onChange={(e) => setDestinataire(e.target.value)}
            />
            <select
              className="block w-full rounded-md border px-2 py-1.5 text-sm"
              style={inputStyle}
              value={type}
              onChange={(e) => onChangeType(e.target.value as TypeMessage)}
            >
              {TYPES_MESSAGE.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            {type === "AUTRE" && (
              <input
                className="block w-full rounded-md border px-2 py-1.5 text-sm"
                style={inputStyle}
                placeholder="Justification obligatoire (ex. demande d'expertise)…"
                value={sousType}
                onChange={(e) => setSousType(e.target.value)}
              />
            )}
            <div className="flex items-center gap-2">
              <select
                className="block w-full rounded-md border px-2 py-1.5 text-sm disabled:opacity-60"
                style={inputStyle}
                value={visibiliteEffective}
                disabled={imposee}
                onChange={(e) =>
                  setVisibilite(e.target.value as VisibiliteMessage)
                }
              >
                {(Object.keys(VISIBILITES) as VisibiliteMessage[]).map((v) => (
                  <option key={v} value={v}>
                    {VISIBILITES[v]}
                  </option>
                ))}
              </select>
              {imposee && (
                <span className="shrink-0 text-[10px]" style={{ opacity: 0.6 }}>
                  imposée
                </span>
              )}
            </div>
            <textarea
              className="block w-full rounded-md border px-2 py-1.5 text-sm"
              style={inputStyle}
              rows={3}
              placeholder="Contenu du message…"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onSubmit}
                disabled={busy || !canSend}
                className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                style={{ background: GOLD, color: GRAPHITE }}
              >
                <Send size={13} /> Envoyer
              </button>
            </div>
          </>
        )}
      </div>

      {messages.length === 0 ? (
        <p className="text-sm" style={{ opacity: 0.7 }}>
          Aucun échange protocolaire.
        </p>
      ) : (
        <ul className="space-y-2">
          {messages.map((m) => {
            const statut = m.statut as StatutMessage;
            const suivants = TRANSITIONS[statut] ?? [];
            return (
              <li
                key={m.id}
                className="rounded-md border p-3"
                style={{ borderColor: BORDER }}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Mail size={14} style={{ color: GOLD }} />
                  <span
                    className="text-xs font-bold uppercase tracking-wider"
                    style={{ color: GOLD }}
                  >
                    {typeMessageLabel(m.type_message)}
                  </span>
                  {m.reply_to_id && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px]"
                      style={{ opacity: 0.6 }}
                    >
                      <Reply size={10} /> réponse
                    </span>
                  )}
                  <span className="ml-auto text-[10px]" style={{ opacity: 0.6 }}>
                    #{m.dossier_id}
                  </span>
                </div>
                {m.sous_type && (
                  <div className="mt-0.5 text-[11px]" style={{ opacity: 0.75 }}>
                    {m.sous_type}
                  </div>
                )}
                {m.content && (
                  <p className="mt-1 text-sm whitespace-pre-wrap">{m.content}</p>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <Badge>{STATUTS_MESSAGE[statut] ?? m.statut}</Badge>
                  <Badge>
                    {VISIBILITES[m.visibilite as VisibiliteMessage] ??
                      m.visibilite}
                  </Badge>
                  {suivants
                    .filter((c) => peutTransitionner(c, m, currentAvocatId))
                    .map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => onTransition(m, c)}
                        className="rounded-md border px-2 py-0.5 text-[10px]"
                        style={{ borderColor: BORDER, color: GOLD }}
                      >
                        → {STATUTS_MESSAGE[c]}
                      </button>
                    ))}
                  <button
                    type="button"
                    onClick={() => {
                      setReplyTo(m);
                      setDossierId(m.dossier_id);
                      setDestinataire(m.expediteur_id);
                    }}
                    className="ml-auto inline-flex items-center gap-1 text-[10px]"
                    style={{ opacity: 0.7 }}
                  >
                    <Reply size={11} /> Répondre
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="rounded-full border px-2 py-0.5 text-[10px]"
      style={{ borderColor: BORDER, opacity: 0.85 }}
    >
      {children}
    </span>
  );
}
